import React from 'react'
import { useForm, Link, usePage } from '@inertiajs/react'
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Grid,
  Breadcrumbs,
  Alert,
} from '@mui/material'
import ManagementLayout from '@/components/layouts/ManagementLayout'
import type { Account } from '@/types'

interface Props {
  backend: {
    name: string
    backend_type: string
    service: string
    bucket: string
    region: string
    endpoint: string
    ssl_verify_peers: boolean
    storage_path: string
  }
}

export default function BackendsNew({ backend }: Props) {
  const { currentAccount } = usePage<{ currentAccount: Account }>().props

  const { data, setData, post, processing, errors } = useForm({
    name: backend.name || '',
    backend_type: backend.backend_type || 's3',
    service: backend.service || 'aws',
    bucket: backend.bucket || '',
    region: backend.region || 'us-east-1',
    endpoint: backend.endpoint || '',
    access_key: '',
    secret_access_key: '',
    ssl_verify_peers: backend.ssl_verify_peers ?? true,
    storage_path: backend.storage_path || '',
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    post(`/accounts/${currentAccount.slug}/backends`)
  }

  const isS3Backend = data.backend_type === 's3'
  const isLocalBackend = data.backend_type === 'local'

  return (
    <ManagementLayout>
      <Box sx={{ maxWidth: 672, mx: 'auto' }}>
        <Breadcrumbs sx={{ mb: 2 }}>
          <Link href={`/accounts/${currentAccount.slug}/backends`}>Backends</Link>
          <Typography color="text.primary">New Backend</Typography>
        </Breadcrumbs>

        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" fontWeight={600}>
            Add Backend
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Connect a new storage backend for your files.
          </Typography>
        </Box>

        <Paper sx={{ p: 3 }}>
          <Box component="form" onSubmit={handleSubmit}>
            <TextField
              label="Name"
              fullWidth
              value={data.name}
              onChange={(e) => setData('name', e.target.value)}
              error={!!errors.name}
              helperText={errors.name || 'A friendly name for this backend.'}
              placeholder="Production Storage"
              sx={{ mb: 3 }}
            />

            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel>Backend Type</InputLabel>
              <Select
                value={data.backend_type}
                label="Backend Type"
                onChange={(e) => setData('backend_type', e.target.value)}
              >
                <MenuItem value="s3">S3 Compatible Storage</MenuItem>
                <MenuItem value="local">Local Filesystem</MenuItem>
              </Select>
            </FormControl>

            {isLocalBackend && (
              <Alert severity="info" sx={{ mb: 3 }}>
                Local storage saves files to a directory on the server filesystem. Metadata is stored in a SQLite database alongside the files.
              </Alert>
            )}

            {isS3Backend && (
              <>
                <FormControl fullWidth sx={{ mb: 3 }}>
                  <InputLabel>Service</InputLabel>
                  <Select
                    value={data.service}
                    label="Service"
                    onChange={(e) => setData('service', e.target.value)}
                  >
                    <MenuItem value="aws">Amazon S3</MenuItem>
                    <MenuItem value="minio">MinIO</MenuItem>
                    <MenuItem value="other">Other S3-Compatible</MenuItem>
                  </Select>
                </FormControl>

                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Bucket"
                      fullWidth
                      value={data.bucket}
                      onChange={(e) => setData('bucket', e.target.value)}
                      error={!!errors.bucket}
                      helperText={errors.bucket || 'The actual S3 bucket name.'}
                      placeholder="my-bucket"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Region"
                      fullWidth
                      value={data.region}
                      onChange={(e) => setData('region', e.target.value)}
                      error={!!errors.region}
                      helperText={errors.region || 'AWS region or equivalent.'}
                      placeholder="us-east-1"
                    />
                  </Grid>
                </Grid>

                <TextField
                  label="Endpoint"
                  fullWidth
                  value={data.endpoint}
                  onChange={(e) => setData('endpoint', e.target.value)}
                  error={!!errors.endpoint}
                  helperText={errors.endpoint || 'Custom endpoint URL for S3-compatible services. Leave blank for AWS S3.'}
                  placeholder="https://s3.example.com"
                  sx={{ mb: 3 }}
                />

                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Access Key"
                      fullWidth
                      value={data.access_key}
                      onChange={(e) => setData('access_key', e.target.value)}
                      error={!!errors.access_key}
                      helperText={errors.access_key || 'AWS Access Key ID.'}
                      placeholder="AKIA..."
                      autoComplete="off"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Secret Access Key"
                      type="password"
                      fullWidth
                      value={data.secret_access_key}
                      onChange={(e) => setData('secret_access_key', e.target.value)}
                      error={!!errors.secret_access_key}
                      helperText={errors.secret_access_key || 'AWS Secret Access Key.'}
                      placeholder="wJalrXUtnFEMI..."
                      autoComplete="off"
                    />
                  </Grid>
                </Grid>

                <FormControlLabel
                  control={
                    <Checkbox
                      checked={data.ssl_verify_peers}
                      onChange={(e) => setData('ssl_verify_peers', e.target.checked)}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2" fontWeight={500}>
                        Verify SSL certificates
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Enable SSL certificate verification. Disable only for self-signed certificates.
                      </Typography>
                    </Box>
                  }
                  sx={{ alignItems: 'flex-start', mb: 3 }}
                />
              </>
            )}

            {isLocalBackend && (
              <TextField
                label="Storage Path"
                fullWidth
                value={data.storage_path}
                onChange={(e) => setData('storage_path', e.target.value)}
                error={!!errors.storage_path}
                helperText={errors.storage_path || 'Relative path under storage/local_backends/ where files will be stored.'}
                placeholder="my-files"
                sx={{ mb: 3 }}
              />
            )}

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
              <Button component={Link} href={`/accounts/${currentAccount.slug}/backends`}>
                Cancel
              </Button>
              <Button type="submit" variant="contained" disabled={processing}>
                Create Backend
              </Button>
            </Box>
          </Box>
        </Paper>
      </Box>
    </ManagementLayout>
  )
}
